import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "./Shop.css";
import Footer from "./Footer";
import Pagination from "./Pagination";
import {BASE_URL} from "./Constants";


const Shop = () => {
  const [products, setProducts] = useState([]);
  const [price, setPrice] = useState(100000);
  const [currentPage, setCurrentPage] = useState(1);
  const [user, setUser] = useState(null);
  const [filterOptions, setFilterOptions] = useState({
    categories: [],
    subCategories: [],
    brands: [],
  });
  const [brandFilter, setBrandFilter] = useState("");
  // const [subCategoryFilter, setSubCategoryFilter] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [productsPerPage, setProductsPerPage] = useState(12);
  const navigate = useNavigate();

  const useQuery = () => new URLSearchParams(useLocation().search);
  const query = useQuery();
  const categoryFilter = query.get("category") || "";


  const [, setMessage] = useState("");
  const [, setMessageType] = useState("");
  const [wishlistStatus, setWishlistStatus] = useState({});
  const [totalCount, setTotalCount] = useState(0);


  useEffect(() => {
    const fetchUser = async () => {
      try {
        const res = await fetch(`${BASE_URL}/get_user_details`, {
          method: "GET",
          credentials: "include", // send HTTP-only cookie
        });
        if (!res.ok) throw new Error("Not logged in");
        const data = await res.json();
        setUser(data); // { user_id, email }
      } catch (err) {
        console.log("No user logged in:", err.message);
        setUser(null);
      }
    };
    fetchUser();
  }, []);

  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const params = new URLSearchParams();
        if (categoryFilter) params.append("category", categoryFilter);
        if (brandFilter) params.append("brand", brandFilter)

        const res = await fetch(`${BASE_URL}/filter_options?${params.toString()}`, {
          method: "GET",
          credentials: "include", // send HTTP-only cookie
          headers: { "Content-Type": "application/json"}
        });
        const data = await res.json();

        setFilterOptions({
          categories: data.category || [],
          brands: data.brand || []
        });

        setBrandFilter((prev) =>
          data.brand?.includes(prev) ? prev : ""
        );
      } catch (err) {
        console.log("Error fetching filter options:", err);
      }
    };
    fetchFilterOptions();
  }, [categoryFilter, brandFilter]);


  // now we are fetching items by page
  useEffect(() => {
    const fetchProducts = async () => {
      try {

        const params = new URLSearchParams({
          page_no: currentPage,
          sort_column: "item_id",
          sort_order: "asc",
        });

        if (categoryFilter) params.append("category", categoryFilter);
        if (brandFilter) params.append("brand", brandFilter);
        if (price < 100000) params.append("max_price", price);

        const res = await fetch(
          `${BASE_URL}/fetch_item_by_page?${params.toString()}`,
          {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
          }
        );
        const data = await res.json();
        const items = data.items || [];

        setProducts(items);
        setTotalCount(data.total_count || 0);
        setProductsPerPage(data.page_size || 12);

      } catch (err) {
        console.error("Error fetching products:", err);
      }
    };
    fetchProducts();
  }, [currentPage, categoryFilter, brandFilter, price]);

  useEffect(() => {
    setCurrentPage(1);
  }, [categoryFilter, brandFilter, price]);


  const handlePriceChange = (event) => setPrice(Number(event.target.value));

  const handleBrandChange = (brand) => {
    setBrandFilter((prev) => (prev === brand ? "" : brand));
  };


  const handleClearAll = () => {
    setBrandFilter([]);
    // setSubCategoryFilter([]);
    setPrice(100000);
    navigate("/shop");
  };

  const handleAddToCart = async (product) => {
    if (!user) {
      // alert("Please login to add items to cart");
      setMessage("Please login to add items to cart")
      setMessageType("error")
      navigate("/checkout/login");
      return;
    }

    try {
      // Fetch or create user cart
      let cartId;
      const resCart = await fetch(`${BASE_URL}/cart/fetch_cart_details?user_id=${user.user_id}`, {
        method: "GET",
        credentials: "include",
      });

      if (resCart.status === 404) {
        const createRes = await fetch(`${BASE_URL}/cart/create_cart`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ user_id: user.user_id }),
        });
        const data = await createRes.json();
        cartId = data.cart_id;
      } else {
        const data = await resCart.json();
        cartId = data.cart_id;
      }

      const imageUrl = product.images?.length
      ? `${BASE_URL}/download_images/${
          typeof product.images[0] === "object" ? product.images[0].id : product.images[0]
        }`
      : "/assets/default.jpg";

      const payload = {
        cart_id: Number(cartId),
        item_id: Number(product.item_id || product.id),
        quantity: 1,
        name: product.name,
        // price: product.price,
        // gst: product.gst_percentage,
        price: Number(product.price),
        image: imageUrl
      };

      const resAdd = await fetch(`${BASE_URL}/cart_items/add_items_to_cart`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      if (!resAdd.ok) throw new Error("Failed to add item to cart");

      window.dispatchEvent(new CustomEvent("cartUpdated", { detail: { user_id: user.user_id } }));
      setMessage(`${product.name} added to wishlist!`)
      setMessageType("success")
    } catch (err) {
      setMessage(`Failed to add item to wishlist: ${err.message}`)
      setMessageType("error")
    }
  };


  const handleAddToWishlist = async (product) => {
    if (!user) {
      setMessage("Please login to add items to wishlist")
      setMessageType("error")
      navigate("/checkout/login");
      return;
    }

    try {
      // Check if wishlist exists for user (optional)
      let wishlistId;
      const resWishlist = await fetch(`${BASE_URL}/wishlist/fetch_wishlist_details?user_id=${user.user_id}`, {
        method: "GET",
        credentials: "include",
      });

      if (resWishlist.status === 404) {
        // Create wishlist for user if it doesn't exist
        const createRes = await fetch(`${BASE_URL}/wishlist/create_wishlist`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ user_id: user.user_id }),
        });
        const data = await createRes.json();
        wishlistId = data.wishlist_id;
      } else {
        const data = await resWishlist.json();
        wishlistId = data.wishlist_id;
      }

      // Handle image URL if product has multiple images
      const imageUrl = product.images?.length
        ? `${BASE_URL}/download_images/${
            typeof product.images[0] === "object" ? product.images[0].id : product.images[0]
          }`
        : "/assets/default.jpg";

      const payload = {
        wishlist_id: wishlistId,
        item_id: product.item_id,
        price: product.price,
        image: imageUrl,
      };

      // Add item to backend wishlist
      const resAdd = await fetch(`${BASE_URL}/wishlist_items/add_items_to_wishlist`, {
        method: "POST",
        headers: { "Content-Type": "application/json", },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      const data = await resAdd.json();

      if (!resAdd.ok) {
        setMessage(data.error || "Failed to add item");
        setMessageType("error");
        return;
      }

      if (data.already_exists) {
        setWishlistStatus((prev) => ({
          ...prev,
          [product.item_id]: "exists",
        }));
        setMessage(data.message || `${product.name} is already in wishlist`);
        setMessageType("info");

        return;
      }

      // success
      setWishlistStatus((prev) => ({
        ...prev,
        [product.item_id]: "added",
      }));
      setMessage(data.message || `${product.name} added to wishlist!`);
      setMessageType("success");

    } catch (err) {
      setMessage("Something went wrong");
      setMessageType("error");
    }
  };


  return (
    <>
      <div className="careers-wrapper">
        {/* ✅ Mobile Filter Toggle */}
        <button
          className="filter-toggle-btn"
          onClick={() => setShowFilters(!showFilters)}
        >
          {showFilters ? "Hide Filters" : "Show Filters"}
        </button>
        {/* ---------- Sidebar Filters ---------- */}
        <aside className={`filter-sidebar ${showFilters ? "show" : ""}`}>
          <h3>
            Filter By{" "}
            <span className="clear" onClick={handleClearAll}>
              Clear all
            </span>
          </h3>


          {/* Categories */}
          <div className="filter-section">
            <h4>CATEGORIES</h4>

            {filterOptions.categories.map((cat) => (
              <label
                key={cat}
                className="filter-option"
                onClick={() => navigate(`/shop?category=${encodeURIComponent(cat)}`)}
              >
              <input
                type="checkbox"
                checked={categoryFilter === cat}
                readOnly
                />
                <span>{cat}</span>
              </label>
            ))}
          </div>
          <hr />

          {/* Sub-Categories */}
          {/* <div className="filter-section">
            <h4>SUB-CATEGORIES</h4>
            {filterOptions.subCategories.map((sub) => (
              <label key={sub}>
                <input
                  type="checkbox"
                  checked={subCategoryFilter.includes(sub)}
                  onChange={() => handleSubCategoryChange(sub)}
                />{" "}
                {sub}
              </label>
            ))}
          </div>
          <hr/> */}

          {/* Brands */}
          <div className="filter-section">
            <h4>BRANDS</h4>
            {filterOptions.brands.map((brand) => (
              <label key={brand}>
                <input
                  type="checkbox"
                  checked={brandFilter.includes(brand)}
                  onChange={() => handleBrandChange(brand)}
                />{" "}
                {brand}
              </label>
            ))}
          </div>
          <hr/>

          {/* Price */}
          <div className="filter-section">
            <h4>PRICE</h4>
            <div className="price-slider">
              <input
                type="range"
                min="200"
                max="100000"
                step="100"
                value={price}
                onChange={handlePriceChange}
              />
              <div className="price-lable">
                <span>₹200</span> - <span>₹{price}</span>
              </div>
            </div>
          </div>
        </aside>


        {/* ---------- Product List ---------- */}
        <div className="product-list">
          <h2>Products {categoryFilter && `- ${categoryFilter}`}</h2>

          <div className="product-grid">
            {products.map((item) => (
              <div className="product-card" key={item.item_id}>
                <div
                  onClick={() =>
                    navigate(`/product/${item.item_id}`, {
                      state: { product: item, allProducts: products },
                    })
                  }
                >
                  {item.images?.length > 0 && (
                    <img
                      src={`${BASE_URL}/download_images/${
                        typeof item.images[0] === "object"
                          ? item.images[0].id
                          : item.images[0]
                      }`}
                      alt={item.name}
                    />
                  )}
                  <h3>{item.name}</h3>
                  <div className="star-rating">
                    {[...Array(5)].map((_, i) => (
                      <span
                        key={i}
                        className={`fa fa-star ${
                          i < (item.rating || 4) ? "checked" : ""
                        }`}
                      ></span>
                    ))}
                    <span>(0)</span>
                  </div>
                  <p className="pricing">₹ {item.price} (Excl.GST)</p>
                </div>
                {item.quantity > 0 && (
                  <button className="add-to-cart" onClick={() => handleAddToCart(item)}>
                      Add to Cart
                  </button>
                )}
                <button onClick={() => handleAddToWishlist(item)}>
                    {wishlistStatus[item.item_id] === "added"
                    ? "Added to Wishlist"
                    : wishlistStatus[item.item_id] === "exists"
                    ? "Already in Wishlist"
                    : "Add to Wishlist"}
                </button>
              </div>
            ))}
          </div>

          {/* Pagination */}
          <Pagination
            page={currentPage}
            total={totalCount}
            pageSize={productsPerPage}
            onPageChange={(newPage) => setCurrentPage(newPage)}
          />
        </div>
      </div>
      <Footer />
    </>
  );
};

export default Shop;